import React from 'react'
import {WidgetProps} from '@wix/yoshi-flow-editor'
import {Text, ThreeDotsLoader} from 'wix-ui-tpa/cssVars'
import {RichContent} from '@wix/ricos'
import {useSettings, useStyles} from '@wix/tpa-settings/react'

import {LAYOUT_BREAKPOINTS_START} from '../constants'
import {withExperienceDetailsStorageProvider} from '../storage'
import {Breadcrumbs} from '../../../components-shared/Breadcrumbs'
import {withLayoutSizeProvider} from '../../../components-shared/LayoutSizeProvider'
import {TimeSlotsList} from '../../../components-shared/TimeSlotsList'
import {Header} from '../components/Header'
import {Form} from '../components/Form'
import {RicosViewer} from '../components/RicosViewer'
import {RequestStatus} from '../../../utils/wrapRequest'
import {TimeSlotsPageType} from '../../../utils/getTimeSlotsPageType'
import settingsParams from '../settingsParams'
import {ReserveButton} from '../components/ReserveButton'
import {TagsList} from '../../../components-shared/TagsList'
import {textsSettingsParams} from '../Settings/Tabs/Texts/settingsParams'
import stylesParams from '../stylesParams'
import {TimeSlotsListExtended} from '../../../components-shared/TimeSlotsListExtended'

import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'
import {experienceDetailsDataHooks} from './constants'

const Widget: React.FC<WidgetProps> = () => {
  const {
    t,
    submitButtonContentWidth,
    layoutSize,
    breadcrumbsConfig,
    experience,
    getExperienceStatus,
    isInitialDataInProgress,
    showReservationForm,
    isManualApproval,
    msid,
    // Reservation form data
    partySizeRef,
    partySizeOptions,
    partySize,
    selectedDate,
    selectedTimeSlot,
    dayTimeSlots,
    timeSlotsByDate,
    experiencePageType,
    showNoAvailableTimeSlotNotification,
    showSelectTimeSlotError,
    regionalSettings,
    richContent,
    handleReservationDataStatus,
    // Handlers
    handlePartySizeChange,
    handleDateChange,
    handleFilterDate,
    handleTimeSlotChange,
    handleSubmit,
    // Scroll functionality
    reservationSectionRef,
    scrollToReservationSection,
  } = useHooks()
  const settings = useSettings()
  const styles = useStyles()

  const showBreadcrumbs: boolean = settings.get(settingsParams.showBreadcrumbs)
  const showAboutSection: boolean = settings.get(settingsParams.showAboutSection)
  const aboutSectionTitleText: string = settings.get(settingsParams.aboutSectionTitleText)
  const reservationSectionTitleText: string = settings.get(
    textsSettingsParams.reservationSectionTitleText,
  )
  const reserveNowButtonText: string = settings.get(settingsParams.reserveNowButtonText)
  const requestNowButtonText: string = settings.get(settingsParams.requestNowButtonText)
  const noReservationText: string = settings.get(settingsParams.noReservationText)

  const secondaryTitleTag =
    styles.get(stylesParams.generalSecondaryTitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.generalSecondaryTitleFont).htmlTag

  if (
    (getExperienceStatus === RequestStatus.FAILED ||
      getExperienceStatus === RequestStatus.RESOLVED) &&
    !experience
  ) {
    return <Text>{t('uou-experiences.experience-details.not-found')}</Text>
  }

  return (
    <div
      className={st(classes.root, {[layoutSize]: true})}
      data-hook={experienceDetailsDataHooks.root()}
    >
      {showBreadcrumbs && (
        <div className={st(classes.breadcrumbs)}>
          <Breadcrumbs items={breadcrumbsConfig} />
        </div>
      )}

      <Header
        experience={experience}
        onReserveClick={scrollToReservationSection}
        regionalSettings={regionalSettings}
        layoutSize={layoutSize}
        showReserveButton={showReservationForm}
        isManualApproval={isManualApproval}
      />
      <div className={st(classes.content)}>
        {showAboutSection && richContent && (
          <div className={st(classes.contentSection)}>
            <Text tagName="h2" className={st(classes.sectionTitle, classes.aboutSectionTitle)}>
              {aboutSectionTitleText}
            </Text>
            <RicosViewer content={richContent as unknown as RichContent} msid={msid} />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div
            className={st(classes.contentSection, classes.reservationSection)}
            ref={reservationSectionRef}
          >
            <Text
              tagName="h2"
              className={st(classes.sectionTitle, classes.reservationSectionTitle)}
            >
              {reservationSectionTitleText}
            </Text>

            {showReservationForm ? (
              <>
                <Form
                  ref={partySizeRef}
                  layoutSize={layoutSize}
                  regionalSettings={regionalSettings}
                  partySize={{
                    value: partySize.toString(),
                    options: partySizeOptions,
                    onChange: handlePartySizeChange,
                  }}
                  date={{
                    value: selectedDate,
                    filterDate: handleFilterDate,
                    onChange: handleDateChange,
                  }}
                />
                <div>
                  {(experiencePageType === TimeSlotsPageType.DEFAULT ||
                    experiencePageType === TimeSlotsPageType.EMPTY) && (
                    <TimeSlotsList
                      layoutSize={layoutSize}
                      selectedDate={selectedDate}
                      partySize={partySize}
                      regionalSettings={regionalSettings}
                      items={dayTimeSlots}
                      selectedTimeSlot={selectedTimeSlot}
                      showSelectTimeSlotError={showSelectTimeSlotError}
                      showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                      onSelectTimeSlot={handleTimeSlotChange}
                      renderTagsList={(props) => (
                        <TagsList boxSelectionClassName={classes.boxSelection} {...props} />
                      )}
                      notificationClassName={classes.timeSlotNotificationBodyText}
                    />
                  )}

                  {experiencePageType === TimeSlotsPageType.EXTENDED && (
                    <TimeSlotsListExtended
                      titleTag={secondaryTitleTag}
                      layoutSize={layoutSize}
                      regionalSettings={regionalSettings}
                      items={timeSlotsByDate}
                      partySize={partySize}
                      selectedDate={selectedDate}
                      selectedTimeSlot={selectedTimeSlot}
                      showSelectTimeSlotError={showSelectTimeSlotError}
                      showNoAvailableTimeSlotNotification={showNoAvailableTimeSlotNotification}
                      onSelectTimeSlot={handleTimeSlotChange}
                      titleClassName={classes.timeSlotsListExtendedTitle}
                      notificationClassName={classes.timeSlotNotificationBodyText}
                      renderTagsList={(tagsListProps) => (
                        <TagsList boxSelectionClassName={classes.boxSelection} {...tagsListProps} />
                      )}
                    />
                  )}
                  {experiencePageType !== TimeSlotsPageType.EMPTY && (
                    <ReserveButton
                      width={submitButtonContentWidth}
                      className={st(classes.reserveButton)}
                    >
                      {isInitialDataInProgress ||
                      handleReservationDataStatus === RequestStatus.LOADING ? (
                        <ThreeDotsLoader className={classes.loader} />
                      ) : isManualApproval ? (
                        requestNowButtonText
                      ) : (
                        reserveNowButtonText
                      )}
                    </ReserveButton>
                  )}
                </div>
              </>
            ) : (
              <Text>{noReservationText}</Text>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default withLayoutSizeProvider(
  withExperienceDetailsStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)
